import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, EuroIcon, Palette, Download, Trash2, Save, Database, Sparkles } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { useTheme } from '../../shared/providers/ThemeProvider';
import { toast } from '../../shared/components/Toast';
import { ConfirmDialog } from '../../shared/components/ConfirmDialog';
import { createDemoDeals, seedStores } from '../../shared/data/seedData';
export function SettingsPage() {
  const navigate = useNavigate();
  const {
    theme,
    setTheme
  } = useTheme();
  const {
    settings,
    monthlyBudget,
    updateSettings,
    setMonthlyBudget,
    setInventory,
    setShoppingList,
    setTransactions,
    setActiveDeals,
    stores,
    setStores
  } = useAppStore();
  const [budgetInput, setBudgetInput] = useState(String(monthlyBudget));
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const handleSaveBudget = () => {
    const value = Number(budgetInput);
    if (!isNaN(value) && value > 0) {
      setMonthlyBudget(value);
      toast.success('Budget succesvol opgeslagen');
    } else {
      toast.error('Voer een geldig bedrag in');
    }
  };
  const handleToggleNotification = (key: keyof typeof settings.notifications) => {
    updateSettings({
      notifications: {
        ...settings.notifications,
        [key]: !settings.notifications[key]
      }
    });
  };
  const handleExportData = () => {
    try {
      const state = useAppStore.getState();
      const exportData = {
        inventory: state.inventory,
        shoppingList: state.shoppingList,
        transactions: state.transactions,
        deals: state.activeDeals,
        stores: state.stores,
        settings: state.settings,
        monthlyBudget: state.monthlyBudget
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulkrr-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Data succesvol geëxporteerd');
    } catch (err) {
      toast.error('Er is een fout opgetreden bij het exporteren');
    }
  };
  const handleLoadDemoDeals = () => {
    const demoStores = stores.length > 0 ? stores : seedStores;
    const demoDeals = createDemoDeals(demoStores);
    if (stores.length === 0) {
      setStores(seedStores);
    }
    if (demoDeals.length === 0) {
      toast.error('Voeg eerst een winkel toe om demo-aanbiedingen te bekijken');
      return;
    }
    setActiveDeals(demoDeals);
    toast.success(`${demoDeals.length} demo-aanbiedingen geladen`);
  };
  const handleResetData = () => {
    setInventory([]);
    setShoppingList([]);
    setTransactions([]);
    setActiveDeals([]);
    setShowResetConfirm(false);
    toast.success('Alle data is succesvol gewist');
  };
  return <div className="min-h-screen bg-background pb-24" data-id="element-1944" data-mp="12z2pl8m2ijwf">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border" data-id="element-1945" data-mp="12x9jpvja43ph">
        <div className="p-6" data-id="element-1946" data-mp="y09jw2d0vx6t">
          <div className="flex items-center gap-3" data-id="element-1947" data-mp="t0mxb2bb14lc">
            <button onClick={() => navigate('/profile')} className="p-2 rounded-lg bg-card text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-id="element-1948" data-mp="t1yblw2dluqp">
              <ArrowLeft className="w-5 h-5" data-id="element-1949" data-mp="1lpu40okwx0ho" />
            </button>
            <h1 className="text-2xl font-bold text-foreground" data-id="element-1950" data-mp="1gxtzsjxt7lfa">Instellingen</h1>
          </div>
        </div>
      </div>

      <div className="px-6 pt-6 space-y-8" data-id="element-1951" data-mp="1bypsxzycxzs4">
        {/* Budget Section */}
        <section className="space-y-4" data-id="element-1952" data-mp="29f6c1l4i72ie">
          <div className="flex items-center gap-2 text-primary" data-id="element-1953" data-mp="29g7q7ygtg54c">
            <EuroIcon className="w-5 h-5" data-id="element-1954" data-mp="24n558eq3u3zr" />
            <h2 className="text-lg font-semibold text-foreground" data-id="element-1955" data-mp="24m3kfijae34u">Budget</h2>
          </div>
          <div className="bg-card border border-border rounded-2xl p-5" data-id="element-1956" data-mp="1p89jpzwkz9zl">
            <label className="block text-sm font-medium text-muted-foreground mb-2" data-id="element-1957" data-mp="1t8jnd5wwhoba">
              Maandelijks Budget (€)
            </label>
            <div className="flex gap-3" data-id="element-1958" data-mp="1fabsr4nc6z3a">
              <input type="number" value={budgetInput} onChange={e => setBudgetInput(e.target.value)} className="flex-1 px-4 py-2.5 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" min="0" step="10" data-id="element-1959" data-mp="1k851o5g0y779" />
              <button onClick={handleSaveBudget} className="px-4 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center gap-2" data-id="element-1960" data-mp="235y5d9yh2l5p">
                <Save className="w-4 h-4" data-id="element-1961" data-mp="2vnagg7tfjw78" />
                Opslaan
              </button>
            </div>
          </div>
        </section>

        {/* Notifications Section */}
        <section className="space-y-4" data-id="element-1962" data-mp="f8owu7yy30ug">
          <div className="flex items-center gap-2 text-primary" data-id="element-1963" data-mp="f7nk7z2a66am">
            <Bell className="w-5 h-5" data-id="element-1964" data-mp="sztpur7t4uvt" />
            <h2 className="text-lg font-semibold text-foreground" data-id="element-1965" data-mp="syscuh78dybg">Meldingen</h2>
          </div>
          <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border" data-id="element-1966" data-mp="oyi5p17pd1v0">
            <div className="p-4 flex items-center justify-between" data-id="element-1967" data-mp="18nu0mz4djcc">
              <div data-id="element-1968" data-mp="3sfql7eeyjpmk">
                <p className="font-medium text-foreground" data-id="element-1969" data-mp="3sgjmhzv5dmxb">Meldingen</p>
                <p className="text-sm text-muted-foreground" data-id="element-1970" data-mp="aa58y20sjezg">
                  Ontvang meldingen op je telefoon
                </p>
              </div>
              <button onClick={() => handleToggleNotification('pushEnabled')} className={`w-12 h-6 rounded-full transition-colors relative ${settings.notifications.pushEnabled ? 'bg-primary' : 'bg-accent'}`} data-id="element-1971" data-mp="5cc4pej1sv1t">
                <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${settings.notifications.pushEnabled ? 'left-7' : 'left-1'}`} data-id="element-1972" data-mp="eaymomun77d1" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-between" data-id="element-1973" data-mp="nalnp7jgmb5k">
              <div data-id="element-1974" data-mp="sazl7vnfq2pc">
                <p className="font-medium text-foreground" data-id="element-1975" data-mp="s9f1z26km66s">Aanbieding meldingen</p>
                <p className="text-sm text-muted-foreground" data-id="element-1976" data-mp="3rs7p0vyz6tdj">
                  Krijg een seintje bij nieuwe aanbiedingen
                </p>
              </div>
              <button onClick={() => handleToggleNotification('dealAlerts')} className={`w-12 h-6 rounded-full transition-colors relative ${settings.notifications.dealAlerts ? 'bg-primary' : 'bg-accent'}`} data-id="element-1977" data-mp="4lf4zkio1jjr">
                <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${settings.notifications.dealAlerts ? 'left-7' : 'left-1'}`} data-id="element-1978" data-mp="380ks571aq23f" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-between" data-id="element-1979" data-mp="30imnbfu27f6j">
              <div data-id="element-1980" data-mp="30hbj5ewpc5ew">
                <p className="font-medium text-foreground" data-id="element-1981" data-mp="2vjrtliuk067n">Lage Voorraad</p>
                <p className="text-sm text-muted-foreground" data-id="element-1982" data-mp="2vlw80uq2wnm6">
                  Waarschuwing als iets bijna op is
                </p>
              </div>
              <button onClick={() => handleToggleNotification('lowStockWarnings')} className={`w-12 h-6 rounded-full transition-colors relative ${settings.notifications.lowStockWarnings ? 'bg-primary' : 'bg-accent'}`} data-id="element-1983" data-mp="2mkethgsbktzl">
                <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${settings.notifications.lowStockWarnings ? 'left-7' : 'left-1'}`} data-id="element-1984" data-mp="27uqs6fk51vbv" />
              </button>
            </div>
          </div>
        </section>

        {/* Theme Section */}
        <section className="space-y-4" data-id="element-1985" data-mp="3mqqgf6djcmg2">
          <div className="flex items-center gap-2 text-primary" data-id="element-1986" data-mp="3mrrz9x0smd8c">
            <Palette className="w-5 h-5" data-id="element-1987" data-mp="3rq3bufih31u9" />
            <h2 className="text-lg font-semibold text-foreground" data-id="element-1988" data-mp="3ros98j0rk04q">Weergave</h2>
          </div>
          <div className="bg-card border border-border rounded-2xl p-2 flex gap-2" data-id="element-1989" data-mp="3dp90bo04okrd">
            {(['light', 'dark', 'system'] as const).map(t => <button key={t} onClick={() => setTheme(t)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium capitalize transition-colors ${theme === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`} data-id="element-1990" data-mp="3408xtumf378f">
                {t === 'system' ? 'Systeem' : t === 'light' ? 'Licht' : 'Donker'}
              </button>)}
          </div>
        </section>

        {/* Data Management Section */}
        <section className="space-y-4" data-id="element-1991" data-mp="2q20xrdu33pt1">
          <div className="flex items-center gap-2 text-primary" data-id="element-1992" data-mp="26dmnlxgiv0z3">
            <Database className="w-5 h-5" data-id="element-1993" data-mp="21fbcljehqkfa" />
            <h2 className="text-lg font-semibold text-foreground" data-id="element-1994" data-mp="21gczqm8s6xdd">
              Mijn gegevens
            </h2>
          </div>
          <div className="grid gap-3" data-id="element-1995" data-mp="2fgfgg55crkr1">
            <button onClick={handleLoadDemoDeals} className="flex w-full items-center gap-3 rounded-2xl border border-primary/20 bg-primary/10 p-4 text-left transition-colors hover:bg-primary/15" data-id="element-1996" data-mp="2bdr3bl9lvro9">
              <div className="rounded-lg bg-primary p-2 text-primary-foreground" data-id="element-1997" data-mp="2kde7ufxmtdlj">
                <Sparkles className="h-5 w-5" data-id="element-1998" data-mp="2z21yq8wj8z55" />
              </div>
              <div data-id="element-1999" data-mp="2u40jjz289jyy">
                <p className="font-medium text-foreground" data-id="element-2000" data-mp="2u3qovy68momx">Ververs demo-aanbiedingen</p>
                <p className="text-sm text-muted-foreground" data-id="element-2001" data-mp="383i7kc7p8t79">
                  Laad vijf voorbeelddeals bij je geselecteerde winkels
                </p>
              </div>
            </button>
            <button onClick={handleExportData} className="w-full p-4 bg-card border border-border rounded-2xl flex items-center gap-3 hover:bg-accent transition-colors" data-id="element-2002" data-mp="2u8mdkn56bdh5">
              <div className="p-2 bg-primary/10 text-primary rounded-lg" data-id="element-2003" data-mp="2kijmwtliyblz">
                <Download className="w-5 h-5" data-id="element-2004" data-mp="2fjzzeora8rri" />
              </div>
              <div className="text-left" data-id="element-2005" data-mp="2flt787dmti13">
                <p className="font-medium text-foreground" data-id="element-2006" data-mp="3d7gqdql2u3uo">Maak een backup</p>
                <p className="text-sm text-muted-foreground" data-id="element-2007" data-mp="3d5e5fkys70wt">
                  Download een kopie van je gegevens
                </p>
              </div>
            </button>
            <button onClick={() => setShowResetConfirm(true)} className="w-full p-4 bg-card border border-destructive/30 rounded-2xl flex items-center gap-3 hover:bg-destructive/5 transition-colors" data-id="element-2008" data-mp="337zqxqwf4m75">
              <div className="p-2 bg-destructive/10 text-destructive rounded-lg" data-id="element-2009" data-mp="1mvkl64fz6dh8">
                <Trash2 className="w-5 h-5" data-id="element-2010" data-mp="1mxnjrcv71exk" />
              </div>
              <div className="text-left" data-id="element-2011" data-mp="2euyi70cugg1">
                <p className="font-medium text-destructive" data-id="element-2012" data-mp="7ccyh7we9lbw">Wis Alle Data</p>
                <p className="text-sm text-muted-foreground" data-id="element-2013" data-mp="7aaa0saw409a">
                  Verwijder voorraad, lijsten en deals
                </p>
              </div>
            </button>
          </div>
        </section>
      </div>

      <ConfirmDialog isOpen={showResetConfirm} title="Weet je het zeker?" description="Dit verwijdert al je voorraad, boodschappenlijsten, transacties en deals. Dit kan niet ongedaan worden gemaakt." confirmText="Ja, wis alles" cancelText="Annuleren" variant="danger" onConfirm={handleResetData} onCancel={() => setShowResetConfirm(false)} data-id="element-2014" data-mp="m19djowvbym0" />
    </div>;
}